import React, { useState, useRef } from 'react';
import { useSelector } from 'react-redux';
import { selectUserInfos } from '../../store/userDataStore';
import { selectCourseConfig, selectCourseStats } from '../../store/appDataStore';
import { CircularProgress, Box, Typography } from '@mui/material';
import { useAuth } from '../../providers/AuthProvider';
import { useAuthState } from '../../hooks/useAuthState';
import { userService } from '../../services/userService';
import { tdGroupService } from '../../services/tdGroupService';

import { useAppClasses } from '../../providers/MuiThemeProvider';
import styles from './GlobalResultsPage.module.css';

const GlobalResultsPage = () => {
  const userInfos = useSelector(selectUserInfos);
  const courseConfig = useSelector(selectCourseConfig);
  const courseStats = useSelector(selectCourseStats);
  const projectGroup = userInfos?.project_group;
  const classes = useAppClasses();
  const [tdSelected, setTdSelected] = React.useState('G1');
  const [tdGroups, setTdGroups] = React.useState<string[]>([]);

  // OPTIMISATION: State pour stocker les project groups avec membres (chargés séparément)
  const [projectGroups, setProjectGroups] = React.useState<{[groupId: number]: any}>({});
  // Jeton obtenu à chaque requête via getValidToken() (rafraîchi si besoin) : ne pas garder
  // le jeton brut, il expire pendant qu'une page reste ouverte.
  const { authState, getValidToken } = useAuth();
  const isAuthenticated = authState.isAuthenticated;
  const isTeacher = userInfos?.access === 'teacher' || userInfos?.access === 'admin';

  const [notesLoading, setNotesLoading] = useState(false);
  const [notesError, setNotesError] = useState<string | null>(null);

  const handleDownloadNotes = async () => {
    setNotesLoading(true);
    setNotesError(null);
    try {
      const accessToken = await getValidToken();
      if (!accessToken) return;
      await userService.downloadNotesExcel(accessToken);
    } catch (e) {
      setNotesError('Erreur lors du téléchargement des notes');
    } finally {
      setNotesLoading(false);
    }
  };

  // OPTIMISATION: Charger les project groups avec membres séparément (enseignants uniquement)
  React.useEffect(() => {
    const loadProjectGroups = async () => {
      if (!isTeacher || !isAuthenticated) return;

      try {
        const accessToken = await getValidToken();
        if (!accessToken) return;
        const groups = await userService.getProjectGroups(accessToken);
        const groupsMap: {[groupId: number]: any} = {};
        groups.forEach((group: any) => {
          groupsMap[group.id] = group;
        });
        setProjectGroups(groupsMap);
      } catch (error) {
        console.error('Erreur lors du chargement des groupes projet:', error);
      }
    };

    loadProjectGroups();
  }, [isTeacher, isAuthenticated, getValidToken]);

  // Groupes TD actifs (configurables par le prof — voir Admin Groupes), remplace l'ancienne
  // liste codée en dur pour que la désactivation d'un TD (ex: pas de G5 cette année) le retire
  // bien de ce tableau de bord.
  React.useEffect(() => {
    const loadTdGroups = async () => {
      if (!isAuthenticated) return;
      try {
        const accessToken = await getValidToken();
        if (!accessToken) return;
        const groups = await tdGroupService.list(accessToken);
        setTdGroups(groups.filter((g) => g.active).map((g) => g.code));
      } catch (error) {
        console.error('Erreur lors du chargement des groupes TD:', error);
      }
    };

    loadTdGroups();
  }, [isAuthenticated, getValidToken]);

  return (
    <div>
      <Box className={styles['global-results-page']} >
        <Typography variant="h1" gutterBottom>{courseConfig?.full_name}</Typography>
        <Box className={styles['user-infos-block']} sx={{ display: 'flex', flexDirection: 'column', gap: 0.5, mb: 4 }}>
          {/* Nom */}
          <Box sx={{ minWidth: 180, display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2 }}>
            <Box sx={{ background: '#f5f5f5', px: 2, py: 1, borderRadius: 1, width: 200, minWidth: 200, maxWidth: 200, textAlign: 'left' }}>
              <Typography className={classes.textBold}>Nom</Typography>
            </Box>
            <Box sx={{ px: 2, py: 1, flex: 1 }}>
              <Typography>{userInfos?.lastname}</Typography>
            </Box>
          </Box>
          {/* Prénom */}
          <Box sx={{ minWidth: 180, display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2 }}>
            <Box sx={{ background: '#f5f5f5', px: 2, py: 1, borderRadius: 1, width: 200, minWidth: 200, maxWidth: 200, textAlign: 'left' }}>
              <Typography className={classes.textBold}>Prénom</Typography>
            </Box>
            <Box sx={{ px: 2, py: 1, flex: 1 }}>
              <Typography>{userInfos?.firstname}</Typography>
            </Box>
          </Box>
          {/* Groupe Projet */}
          {projectGroup?.name && (
            <Box sx={{ minWidth: 180, display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2 }}>
              <Box sx={{ background: '#f5f5f5', px: 2, py: 1, borderRadius: 1, width: 200, minWidth: 200, maxWidth: 200, textAlign: 'left' }}>
                <Typography className={classes.textBold}>Groupe Projet</Typography>
              </Box>
              <Box sx={{ px: 2, py: 1, flex: 1 }}>
                <Typography>{projectGroup.name}</Typography>
              </Box>
            </Box>
          )}
          {/* Membres du groupe */}
          {projectGroup?.members?.length > 0 && (
            <Box sx={{ minWidth: 200, display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2 }}>
              <Box sx={{ background: '#f5f5f5', px: 2, py: 1, borderRadius: 1, width: 200, minWidth: 200, maxWidth: 200, textAlign: 'left' }}>
                <Typography className={classes.textBold}>Membres du groupe</Typography>
              </Box>
              <Box sx={{ px: 2, py: 1, flex: 1 }}>
                <ul style={{ margin: 0, paddingLeft: 16 }}>
                  {projectGroup.members.map((member: any) => (
                    <li key={member.user_id}>
                      {member.firstname} {member.lastname} ({member.email})
                    </li>
                  ))}
                </ul>
              </Box>
            </Box>
          )}
          {/* Passphrase + Webtool zip */}
          {projectGroup?.name && (
            <PassphraseField groupId={projectGroup?.id} projectGroupName={projectGroup?.name} />
          )}
        </Box>
        <Box className={styles['cycle-results-block']}>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 2 }}>
            <Typography variant="h2">
              {['teacher', 'admin'].includes(userInfos?.access)
                ? 'Moyenne des scores des cycles'
                : 'Scores des cycles'}
            </Typography>
            {isTeacher && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <button
                  onClick={handleDownloadNotes}
                  disabled={notesLoading}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 8,
                    padding: '8px 16px', borderRadius: 4, border: '1px solid #ccc',
                    background: '#fff', cursor: notesLoading ? 'not-allowed' : 'pointer',
                    opacity: notesLoading ? 0.6 : 1,
                  }}
                >
                  {notesLoading ? '⏳' : '📊'} Télécharger les notes (Excel)
                </button>
                {notesError && (
                  <Typography style={{ color: '#d32f2f', fontWeight: 500, fontSize: '0.95rem' }}>
                    ❌ {notesError}
                  </Typography>
                )}
              </Box>
            )}
          </Box>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
            {courseConfig?.cycles?.map((cycle: any) => {
              if (['teacher', 'admin'].includes(userInfos?.access)) {
                // Version enseignant/admin : CircularProgress = moyenne promo
                const promoMean = courseStats?.cycle_score_mean?.[cycle.id];
                return (
                  <Box key={cycle.id} sx={{ flexBasis: { xs: '100%', sm: '48%', md: '23%' }, display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h3" className={`${styles['cycle-results-title']} ${classes.textBold}`}>{cycle.short_name?.toUpperCase()}</Typography>
                    <CircularProgress size={150} variant="determinate" value={promoMean || 0} sx={{ height: 20, borderRadius: 5 }} />
                    <Typography variant="body1" sx={{ mt: 2, fontWeight: 600, fontSize: '1.1rem' }}>
                      Moyenne : {promoMean !== undefined && promoMean !== null ? promoMean.toFixed(1) : 'N/A'}
                    </Typography>
                  </Box>
                );
              } else {
                // Version étudiant : score du groupe (calculé par l'API, toutes les branches du
                // cycle combinées) + moyenne promo orange
                const cycleScore = projectGroup?.cycle_scores?.[cycle.id]?.score;
                const promoMean = courseStats?.cycle_score_mean?.[cycle.id];
                return (
                  <Box key={cycle.id} sx={{ flexBasis: { xs: '100%', sm: '48%', md: '23%' }, display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h3" className={`${styles['cycle-results-title']} ${classes.textBold}`}>{cycle.short_name?.toUpperCase()}</Typography>
                    <CircularProgress size={150} variant="determinate" value={cycleScore ?? 0} sx={{ height: 20, borderRadius: 5 }} />
                    <Typography variant="body1" sx={{ mt: 2, fontWeight: 600, fontSize: '1.1rem' }}>Score : {cycleScore !== undefined && cycleScore !== null ? cycleScore.toFixed(1) : 'N/A'}</Typography>
                    {promoMean !== undefined && (
                      <Typography variant="caption" sx={{ mt: 1, color: '#ff9800', fontWeight: 600 }}>
                        Moyenne promo : {typeof promoMean === 'number' ? promoMean.toFixed(1) : promoMean}
                      </Typography>
                    )}
                  </Box>
                );
              }
            })}
            {/* Bloc score global projet ou moyenne globale */}
            {['teacher', 'admin'].includes(userInfos?.access)
              ? (
                <Box key="global-score" sx={{ flexBasis: { xs: '100%', sm: '48%', md: '23%' }, display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
                  <Typography variant="h3" className={`${styles['cycle-results-title']} ${classes.textBold}`}>SCORE GLOBAL</Typography>
                  <CircularProgress size={150} variant="determinate" value={courseStats?.global_score || 0} sx={{ height: 20, borderRadius: 5 }} />
                  <Typography variant="body1" sx={{ mt: 2, fontWeight: 600, fontSize: '1.1rem' }}>
                    Moyenne : {courseStats?.global_score !== undefined && courseStats?.global_score !== null ? courseStats.global_score.toFixed(1) : 'N/A'}
                  </Typography>
                </Box>
              ) : (
                projectGroup?.global_score !== undefined && (
                  <Box key="global-score" sx={{ flexBasis: { xs: '100%', sm: '48%', md: '23%' }, display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
                    <Typography variant="h3" className={`${styles['cycle-results-title']} ${classes.textBold}`}>SCORE GLOBAL</Typography>
                    <CircularProgress size={150} variant="determinate" value={projectGroup.global_score} sx={{ height: 20, borderRadius: 5 }} />
                    <Typography variant="body1" sx={{ mt: 2, color: 'black', fontWeight: 600, fontSize: '1.1rem' }}>
                      Score global : {typeof projectGroup.global_score === 'number' ? projectGroup.global_score.toFixed(1) : projectGroup.global_score}
                    </Typography>
                    {courseStats?.global_score !== undefined && (
                      <Typography variant="caption" sx={{ mt: 1, color: '#ff9800', fontWeight: 600 }}>
                        Moyenne promo : {typeof courseStats.global_score === 'number' ? courseStats.global_score.toFixed(1) : courseStats.global_score}
                      </Typography>
                    )}
                  </Box>
                )
              )}
          </Box>
        </Box>
        {['teacher', 'admin'].includes(userInfos?.access) && (
          <Box className={styles['cycle-results-block']} sx={{ mt: 4 }}>
            <Typography variant="h2">Moyenne des scores par groupe TD</Typography>
            {/* Un sous-bloc par cycle */}
            {courseConfig?.cycles?.map((cycle: any) => (
              <Box key={cycle.id} sx={{ mb: 3 }}>
                <Typography variant="h4" sx={{ mb: 1 }}>{cycle.short_name?.toUpperCase()}</Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'flex-start' }}>
                  {tdGroups.map(td => {
                    // Moyenne du TD pour ce cycle (calculée par l'API, branches du cycle combinées)
                    const avg = courseStats?.td_cycle_mean?.[cycle.id]?.[td] ?? null;
                    return (
                      <Box key={td} sx={{ flexBasis: { xs: '48%', sm: '22%', md: '10%' }, maxWidth: 120, display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h5" className={classes.textBold}>{td}</Typography>
                        <CircularProgress size={80} variant="determinate" value={avg ?? 0} sx={{ height: 10, borderRadius: 5 }} />
                        <Typography variant="body1" sx={{ mt: 1, fontWeight: 600, fontSize: '1.1rem' }}>{avg !== null ? avg.toFixed(1) : 'N/A'}</Typography>
                      </Box>
                    );
                  })}
                </Box>
              </Box>
            ))}
            {/* Bloc global (moyenne pondérée) */}
            <Box sx={{ mb: 3 }}>
              <Typography variant="h4" sx={{ mb: 1 }}>SCORE GLOBAL</Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, justifyContent: 'flex-start' }}>
                {(() => {
                  return tdGroups.map(td => {
                    const avg = courseStats?.td_global_mean?.[td] ?? null;
                    return (
                      <Box key={td} sx={{ flexBasis: { xs: '48%', sm: '22%', md: '10%' }, maxWidth: 120, display: 'flex', flexDirection: 'column', alignItems: 'center', mb: 2 }}>
                        <Typography variant="h5" className={classes.textBold}>{td}</Typography>
                        <CircularProgress size={80} variant="determinate" value={avg ?? 0} sx={{ height: 10, borderRadius: 5 }} />
                        <Typography variant="body1" sx={{ mt: 1, fontWeight: 600, fontSize: '1.1rem' }}>{avg !== null ? avg.toFixed(1) : 'N/A'}</Typography>
                      </Box>
                    );
                  });
                })()}
              </Box>
            </Box>
          </Box>
        )}
        {['teacher', 'admin'].includes(userInfos?.access) && (
          <Box className={styles['cycle-results-block']} sx={{ mt: 4 }}>
            <Typography variant="h2">Tableau des groupes projet par TD</Typography>
            {/* Pagination par TD */}
            <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
              {tdGroups.map(td => (
                <Box key={td} sx={{ cursor: 'pointer', px: 2, py: 1, borderRadius: 2, background: tdSelected === td ? '#1976d2' : '#eee', color: tdSelected === td ? '#fff' : '#333', fontWeight: tdSelected === td ? 700 : 400 }}
                  onClick={() => setTdSelected(td)}>
                  {td}
                </Box>
              ))}
            </Box>
            {/* Table MUI */}
            <Box sx={{ overflowX: 'auto' }}>
              <table className={styles['project-table']} style={{ minWidth: 800, width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr>
                    <th style={{ textAlign: 'left', padding: 8 }}>Nom groupe projet</th>
                    <th style={{ textAlign: 'left', padding: 8 }}>Membres</th>
                    {courseConfig?.cycles?.map((cycle: any) => (
                      <th key={cycle.id} style={{ textAlign: 'left', padding: 8 }}>{cycle.short_name?.toUpperCase()}</th>
                    ))}
                    <th style={{ textAlign: 'left', padding: 8 }}>SCORE GLOBAL</th>
                  </tr>
                </thead>
                <tbody>
                  {(() => {
                    // Scores calculés par l'API (GET /project-groups) : un cycle combine ses branches
                    // (cycle préparatoire : prepa-student-A/B et prepa-commun)
                    const cycles = courseConfig?.cycles || [];
                    const byProject = Object.values(projectGroups)
                      .filter((group: any) => Object.keys(group.cycle_scores || {}).length > 0)
                      .map((group: any) => ({
                        name: group.name || '',
                        members: (group.members || []).map((m: any) => `${m.firstname} ${m.lastname}`).join(', '),
                        td_group: group.td_group || '',
                        scores: group.cycle_scores as { [cycleId: string]: { score: number } },
                        global_score: group.global_score as number | null,
                      }));
                    // Filtrer par TD sélectionné
                    const filtered = byProject.filter((proj: any) => proj.td_group === tdSelected);
                    // Trier par nom de groupe projet
                    filtered.sort((a: any, b: any) => a.name.localeCompare(b.name));
                    return filtered.map((proj: any, idx: number) => (
                      <tr key={proj.name + idx}>
                        <td style={{ padding: 8 }}>{proj.name}</td>
                        <td style={{ padding: 8 }}>{proj.members}</td>
                        {cycles.map((cycle: any) => (
                          <td key={cycle.id} style={{ padding: 8 }}>
                            {proj.scores[cycle.id]?.score !== undefined && proj.scores[cycle.id]?.score !== null ? proj.scores[cycle.id].score.toFixed(1) : 'N/A'}
                          </td>
                        ))}
                        <td style={{ padding: 8 }}>{proj.global_score !== undefined && proj.global_score !== null ? proj.global_score.toFixed(1) : 'N/A'}</td>
                      </tr>
                    ));
                  })()}
                </tbody>
              </table>
            </Box>
          </Box>
        )}
      </Box>
    </div>
  );
};

function PassphraseField({ groupId, projectGroupName }: { groupId: number, projectGroupName: string }) {
  const [state, setState] = useState<'masked' | 'loading' | 'revealed'>('masked');
  const [passphrase, setPassphrase] = useState<string>('');
  const [webtoolError, setWebtoolError] = useState<string | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  // Base URL de l'API provenant de l'environnement Vite
  const API_BASE_URL = import.meta.env.VITE_REACT_APP_API_EP_URI as string;

  // Récupère le token JWT depuis le store d'authentification
  const auth = useAuthState();
  const token = auth?.token;

  // Vérifie si le fichier webtool-zip existe au montage du composant
  React.useEffect(() => {
    const checkWebtoolFile = async () => {
      if (!token) {
        console.warn('Token non disponible, impossible de vérifier le fichier webtool');
        return;
      }
      
      try {
        const response = await fetch(`${API_BASE_URL}/project-groups/${groupId}/webtool-zip`, {
          method: 'HEAD',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });
        
        if (!response.ok) {
          // Construire le message d'erreur basé sur le statut HTTP
          if (response.status === 404) {
            setWebtoolError(`Le fichier webtool_${projectGroupName}.zip n'existe pas. Veuillez contacter le responsable du module Technologies WEB.`);
          } else if (response.status === 403) {
            setWebtoolError('Accès refusé au fichier webtool');
          } else {
            setWebtoolError('Erreur lors de l\'accès au fichier webtool');
          }
        }
      } catch (error) {
        // Erreur réseau ou autre, on laisse le lien tel quel
        console.error('Erreur lors de la vérification du fichier webtool:', error);
      }
    };
    
    if (token && API_BASE_URL) {
      checkWebtoolFile();
    }
  }, [groupId, token, API_BASE_URL, projectGroupName]);

  const handleReveal = async () => {
    setState('loading');
    setPassphrase('');
    abortRef.current = new AbortController();
    try {
      const response = await fetch(`${API_BASE_URL}/project-groups/${groupId}/passphrase`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        signal: abortRef.current.signal,
      });
      if (!response.ok) {
        setPassphrase('Erreur');
        setState('revealed');
        return;
      }
      const data = await response.json();
      setPassphrase(data.passphrase || '');
      setState('revealed');
    } catch (e) {
      const error = e as Error;
      if (error.name !== 'AbortError') {
        setPassphrase('Erreur');
        setState('revealed');
      }
    }
  };

  const handleHide = () => {
    if (abortRef.current) abortRef.current.abort();
    setPassphrase('');
    setState('masked');
  };

  const [webtoolLoading, setWebtoolLoading] = useState(false);

  const handleDownloadWebtool = async () => {
    setWebtoolLoading(true);
    try {
      const response = await fetch(`${API_BASE_URL}/project-groups/${groupId}/webtool-zip`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        setWebtoolError(errorData.message || 'Erreur lors du téléchargement');
        return;
      }

      // Créer un blob et déclencher le téléchargement
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `webtool_${projectGroupName}.zip`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Erreur lors du téléchargement du webtool:', error);
      setWebtoolError('Erreur lors du téléchargement');
    } finally {
      setWebtoolLoading(false);
    }
  };

  return (
    <>
      {/* Ligne Passphrase */}
      <Box sx={{ minWidth: 200, display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2 }}>
        <Box sx={{ background: '#f5f5f5', px: 2, py: 1, borderRadius: 1, width: 200, minWidth: 200, maxWidth: 200, textAlign: 'left' }}>
          <Typography style={{ fontWeight: 600 }}>Passphrase</Typography>
        </Box>
        <Box sx={{ px: 2, py: 1, flex: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
          <button
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
            onMouseDown={handleReveal}
            onMouseUp={handleHide}
            onMouseLeave={handleHide}
            aria-label={state === 'revealed' ? 'Masquer le passphrase' : 'Afficher le passphrase'}
          >
            <span role="img" aria-label="œil" style={{ fontSize: 20 }}>
              {state === 'revealed' ? '🙈' : '👁️'}
            </span>
          </button>
          <Typography style={{ minWidth: 100 }}>
            {state === 'masked' && '•••••'}
            {state === 'loading' && 'Déchiffrement passphrase en cours...'}
            {state === 'revealed' && passphrase}
          </Typography>
        </Box>
      </Box>
      {/* Ligne Webtool zip */}
      <Box sx={{ minWidth: 200, display: 'flex', flexDirection: 'row', alignItems: 'center', gap: 2 }}>
        <Box sx={{ background: '#f5f5f5', px: 2, py: 1, borderRadius: 1, width: 200, minWidth: 200, maxWidth: 200, textAlign: 'left' }}>
          <Typography style={{ fontWeight: 600 }}>Webtool</Typography>
        </Box>
        <Box sx={{ px: 2, py: 1, flex: 1, display: 'flex', alignItems: 'center', gap: 2 }}>
          {webtoolError ? (
            <Typography style={{ color: '#d32f2f', fontWeight: 500, fontSize: '0.95rem' }}>
              ❌ {webtoolError}
            </Typography>
          ) : (
            <button
              onClick={handleDownloadWebtool}
              disabled={webtoolLoading}
              style={{
                display: 'flex',
                alignItems: 'center',
                textDecoration: 'none',
                color: '#1976d2',
                fontWeight: 500,
                fontSize: '1rem',
                lineHeight: 1,
                background: 'none',
                border: 'none',
                cursor: webtoolLoading ? 'not-allowed' : 'pointer',
                padding: 0,
                opacity: webtoolLoading ? 0.6 : 1,
              }}
            >
              <span role="img" aria-label="zip" style={{ fontSize: 16, marginRight: 6, verticalAlign: 'middle' }}>
                {webtoolLoading ? '⏳' : '🗜️'}
              </span>
              <span style={{ verticalAlign: 'middle' }}>
                {webtoolLoading ? 'Téléchargement...' : 'Télécharger le zip'}
              </span>
            </button>
          )}
        </Box>
      </Box>
    </>
  );
}

export default GlobalResultsPage;
